import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PartTimePhased } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

interface PartTimePhasedTableProps {
  timePhased: PartTimePhased[];
}

export const PartTimePhasedTable: React.FC<PartTimePhasedTableProps> = ({
  timePhased,
}) => {
  const { t } = useTranslation();

  const totalInbound = timePhased
    .filter((tp) => tp.calculatedTransQty > 0)
    .reduce((sum, tp) => sum + tp.calculatedTransQty, 0);

  const totalOutbound = timePhased
    .filter((tp) => tp.calculatedTransQty < 0)
    .reduce((sum, tp) => sum + Math.abs(tp.calculatedTransQty), 0);

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-orange-50 px-4 py-3 border-b border-orange-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-orange-900">
          {t('parts.timePhased')}
        </h3>
        <p className="text-xs text-orange-700">
          {timePhased.length}{' '}
          {timePhased.length === 1
            ? t('parts.records')
            : t('parts.recordPlural')}
          {(totalInbound > 0 || totalOutbound > 0) && (
            <>
              {' '}
              • <span>IN: {totalInbound.toLocaleString()}</span>{' '}
              <span>OUT: {totalOutbound.toLocaleString()}</span>
            </>
          )}
        </p>
      </div>

      {/* Content */}
      {timePhased.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.dueDate')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.qty')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.cumulative')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.details')}
                </th>
              </tr>
            </thead>
            <tbody>
              {timePhased.map((tp, idx) => {
                // Calculate cumulative running total
                const cumulativeQty = timePhased
                  .slice(0, idx + 1)
                  .reduce((sum, item) => sum + item.calculatedTransQty, 0);

                // Only highlight rows where cumulative drops below 0
                const isBelowZero = cumulativeQty < 0;
                const rowColor = isBelowZero ? 'bg-red-50' : 'bg-white';
                const qtyColor = isBelowZero ? 'text-red-700' : 'text-gray-900';

                return (
                  <tr
                    key={idx}
                    className={`border-b border-gray-100 hover:opacity-75 transition-opacity ${rowColor}`}
                  >
                    <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                      {formatDate(tp.partDtlDueDate)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-bold whitespace-nowrap ${qtyColor}`}
                    >
                      {tp.calculatedTransQty < 0 ? '-' : '+'}
                      {Math.abs(tp.calculatedTransQty).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                      {cumulativeQty < 0 ? '-' : '+'}
                      {Math.abs(cumulativeQty).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-xs truncate max-w-xs">
                      {renderTextWithLinks(tp.calculatedInfo)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500 text-sm flex-grow flex items-center justify-center">
          {t('parts.noTimePhasedData')}
        </div>
      )}
    </div>
  );
};

/**
 * Render text with clickable links for job numbers and order numbers
 * Converts "Job XXXXX" patterns into links to /job-tracker?jobNum=XXXXX
 * Converts "Order XXXXX" or "SO XXXXX" patterns into links to /sales-orders/XXXXX
 * Converts "PO XXXXX" patterns into links to /purchase-orders/XXXXX
 */
function renderTextWithLinks(text: string): React.ReactNode {
  if (!text) return text;

  // Combined pattern to match Job, Order and PO references
  // Group 1: Job number (from "Job XXXXX")
  // Group 2: Order number (from "Order XXXXX")
  // Group 3: Order number (from "SO XXXXX" or "SO-XXXXX")
  // Group 4: PO number (from "PO XXXXX", e.g. "PO 601440 (Supplier)")
  const linkPattern = /Job\s+(\d+)|Order\s+(\d+)|SO[-\s]?(\d+)|\bPO\s+(\d+)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = linkPattern.lastIndex;
    const matchedText = match[0];

    // Add text before the match
    if (matchStart > lastIndex) {
      parts.push(text.substring(lastIndex, matchStart));
    }

    if (match[1]) {
      // Job match
      const jobNum = match[1];
      parts.push(
        <Link
          key={`job-${matchStart}`}
          to={`/job-tracker?jobNum=${jobNum}`}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
        >
          {matchedText}
        </Link>
      );
    } else if (match[4]) {
      // PO match
      parts.push(
        <Link
          key={`po-${matchStart}`}
          to={`/purchase-orders/${match[4]}`}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
        >
          {matchedText}
        </Link>
      );
    } else {
      // Order match (either "Order XXXXX" or "SO XXXXX")
      const orderNum = match[2] || match[3];
      parts.push(
        <Link
          key={`order-${matchStart}`}
          to={`/sales-orders/${orderNum}`}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
        >
          {matchedText}
        </Link>
      );
    }

    lastIndex = matchEnd;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no matches found, return original text
  return parts.length > 0 ? parts : text;
}

/**
 * Format date string for display
 * Handles ISO format, timestamps, and various date formats
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try parsing as ISO date first
    if (dateString.includes('T') || dateString.includes('-')) {
      date = parseCalendarDate(dateString);
    } else if (/^\d+$/.test(dateString)) {
      // Handle timestamp in milliseconds
      date = new Date(parseInt(dateString, 10));
    } else {
      // Try direct Date constructor
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing failed
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}
